import CryptoJS from "crypto-js";
import { key } from "./Urls";

function formatIndianAmount(amount) {
  if (typeof amount !== "number" || isNaN(amount)) return "0.00";

  if (amount >= 1_00_00_000) {
    return `${(amount / 1_00_00_000).toFixed(2)} Cr`;
  }

  if (amount >= 1_00_000) {
    return `${(amount / 1_00_000).toFixed(2)} Lakh`;
  }

  return amount.toFixed(2);
}
function getAvgPricePerSqft(config) {
  const area = config.CarpetArea || config.BuiltUpArea;
  if (!area || !config.Price) return "0";

  const value = Math.round(config.Price / area);
  return value.toLocaleString("en-IN");
}

function formatDaysAgo(dateString) {
  if (!dateString) return "";

  // Expected format: dd-MMM-yyyy (e.g. 12-Jan-2024)
  const parts = dateString.split("-");
  if (parts.length !== 3) return "";

  const [day, monthStr, year] = parts;

  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  const month = monthMap[monthStr];
  if (month === undefined) return "";

  const inputDate = new Date(Number(year), month, Number(day));

  if (isNaN(inputDate.getTime())) return "";

  // Reset time for accurate day comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  inputDate.setHours(0, 0, 0, 0);

  const diffInMillis = today.getTime() - inputDate.getTime();
  const diffInDays = Math.floor(diffInMillis / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays >= 2) return `${diffInDays} days ago`;

  return "";
}

function decryptOtp(encryptedText) {
  if (!encryptedText) return null;

  // Match Kotlin getKeyBytes(): 16 bytes, UTF-8, padded/truncated
  const keyUtf8 = CryptoJS.enc.Utf8.parse(key.padEnd(16, "\0").slice(0, 16));

  const ivUtf8 = keyUtf8; // IV = key (same as Android)

  try {
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(encryptedText) },
      keyUtf8,
      {
        iv: ivUtf8,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7, // PKCS5 == PKCS7 for AES
      },
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    console.error("OTP decrypt failed", e);
    return null;
  }
}
function removeSpecialCharacters(text) {
  return text.replace(/[^a-zA-Z0-9 .,()-]/g, "");
}

function buildFilterString(filterParams = {}) {
  const filters = [];

  Object.entries(filterParams).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;

    // 🔴 SPECIAL CASES (elemMatch)
    if (key === "societyAmenities") {
      filters.push(
        `'PropertyAmenities':{$elemMatch:{'PropertyAmenitiesID':{$in:[${value.join(
          ",",
        )}]}}}`,
      );
      return;
    }

    if (key === "propertyAmenities") {
      filters.push(
        `'UnitFurnishingtitle':{$elemMatch:{'UnitFurnishingID':{$in:[${value.join(
          ",",
        )}]}}}`,
      );
      return;
    }

    // 🟢 NORMAL ARRAY CASE
    if (Array.isArray(value)) {
      filters.push(`'${mapKey(key)}':{$in:[${value.join(",")}]}`);
      return;
    }

    // 🟢 SINGLE VALUE CASE
    filters.push(`'${mapKey(key)}':{$eq:${value}}`);
  });

  return filters.join(",");
}

function mapKey(key) {
  const map = {
    propertyTypeIds: "ChoosePropertyID",
    bhkTypeIds: "BHKID",
    cityIds: "CityID",
    localityIds: "LocalityID",
    areaIds: "SocietyID",
    societyIds: "SocietyID",
    societyAmenities: "PropertyAmenitiesID",
    propertyAmenities: "UnitFurnishingID",
    featured: "FeaturedID",
    my: "cByID",
    vertical: "VerticalID",
    propertyType: "PropertyTypeID",
    constructionStatusIds: "ConstructionStatusID",
    furnishingTypeIds: "FurnishID",
    PGisFor: "PgisforID",
    BestSuitedFor: "BestSuitedforID",
    SocietyTypeIds: "SocietyTypeID",

    projectStatusIds: "ProjectStatusID",
    status: "StatusID",

    devIds: "DeveloperID",
  };

  return map[key] || key;
}

function getDisplayPrice(prop) {
  // Buy (Residential / Commercial)
  if ([1, 2].includes(prop?.PropertyTypeID) && prop?.VerticalID === 1) {
    return `₹ ${formatIndianAmount(Number(prop.Price))}`;
  }

  // Rent (Residential / Commercial)
  if ([1, 2].includes(prop?.PropertyTypeID) && prop?.VerticalID === 2) {
    return `₹ ${formatIndianAmount(Number(prop?.MonthlyRent ?? 0))}`;
  }

  // VerticalID == 3 and PropertyTypeID == 1
  if (prop?.PropertyTypeID === 1 && prop?.VerticalID === 3) {
    return `₹ ${rentRangeString(prop?.R1_RoomType)}`;
  }

  // Default (PG / Room type)
  return `₹ ${formatIndianAmount(Number(prop?.Price))}`;
}

function rentRangeString(roomTypes) {
  const rents = Array.isArray(roomTypes)
    ? roomTypes
        .map((rt) => parseNumberSafe(rt.Rent))
        .filter((v) => v !== null && v !== undefined)
    : [];

  if (rents.length === 0) return "";

  const min = Math.min(...rents);
  const max = Math.max(...rents);

  const minStr = Number.isInteger(min) ? String(min) : String(min);
  const maxStr = Number.isInteger(max) ? String(max) : String(max);

  return min === max ? minStr : `${minStr} - ${maxStr}`;
}

function parseNumberSafe(value) {
  if (value === null || value === undefined) return null;

  // If already a number
  if (typeof value === "number") {
    return isNaN(value) ? null : value;
  }

  // Convert everything else to string
  const s = String(value).trim();
  if (!s || s.toLowerCase() === "null") return null;

  // Remove non-digits except dot
  const digitsOnly = s.replace(/[^0-9.]/g, "");
  if (!digitsOnly) return null;

  const num = Number(digitsOnly);
  return isNaN(num) ? null : num;
}

function getUserLocation(onSuccess, onError) {
  if (!navigator.geolocation) {
    onError?.("Geolocation is not supported by this browser.");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { latitude, longitude } = position.coords;
      onSuccess({ latitude, longitude });
    },
    (error) => {
      onError?.(error.message);
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    },
  );
}

function generate4DigitId() {
  return Math.floor(1000 + Math.random() * 9000);
}

function getFormattedDate() {
  const now = new Date()

  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')

  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}
const formatDisplayDate = (dateString) => {
  if (!dateString) return "";

  const options = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  };

  return new Date(dateString).toLocaleDateString("en-GB", options);
};

 const getUser = () => {
  const cart = localStorage.getItem('UserData-UniversalDairy');
  return cart ? JSON.parse(cart) : {};
};
export {
  formatIndianAmount,
  formatDaysAgo,
  decryptOtp,
  buildFilterString,
  getDisplayPrice,
  getUserLocation,
  getAvgPricePerSqft,
  generate4DigitId,removeSpecialCharacters,getFormattedDate,formatDisplayDate,getUser
};
